/**
 * Firebase Analytics (GA4) 이벤트 로깅 — BigQuery export 로 흘러가는 웹 이용 로그
 *
 * 2026-08-17 Analytics → BigQuery 연동 완료. 여기서 쌓는 이벤트가
 * 사업 증빙·정산용 "서비스 제공 내역(로그 DB)" 의 원천이 된다.
 * SDK 는 호출 시에만 동적 로드 (aiCategoryClassifier / 엑셀·PDF 와 동일 패턴).
 * 개발 모드에서는 기록하지 않고, 실패해도 앱 동작에 영향 없도록 전부 no-op.
 */
import type { Analytics } from 'firebase/analytics';
import app from './firebaseService';

let analyticsPromise: Promise<Analytics | null> | null = null;

async function getAnalyticsInstance(): Promise<Analytics | null> {
  if (!analyticsPromise) {
    analyticsPromise = (async () => {
      try {
        if (import.meta.env.DEV) return null; // 개발 데이터로 프로덕션 로그 오염 방지
        const { getAnalytics, isSupported } = await import('firebase/analytics');
        if (!(await isSupported())) return null;
        return getAnalytics(app);
      } catch {
        return null;
      }
    })();
  }
  return analyticsPromise;
}

/** 공통 이벤트 로깅 — 실패는 조용히 무시 */
export async function trackEvent(
  name: string,
  params?: Record<string, string | number | boolean>,
): Promise<void> {
  try {
    const analytics = await getAnalyticsInstance();
    if (!analytics) return;
    const { logEvent } = await import('firebase/analytics');
    logEvent(analytics, name, params);
  } catch {
    // no-op — 로깅 실패가 기능을 막으면 안 됨
  }
}

/**
 * 로그인 사용자 식별 + 고객층 세그먼트 속성 설정.
 * tier: 'free' | 'premium' | 'service_premium' | 'member' — BigQuery 에서
 * 모든 이벤트가 이 user property 로 자동 세그먼트된다 (이벤트마다 붙일 필요 없음).
 * ⚠️ 개인정보 금지 — 이벤트/속성에 이름·급여액 등 PII 를 절대 싣지 않는다 (uid 만 허용).
 */
export function setAnalyticsUser(uid: string, tier: string): void {
  void (async () => {
    try {
      const analytics = await getAnalyticsInstance();
      if (!analytics) return;
      const { setUserId, setUserProperties } = await import('firebase/analytics');
      setUserId(analytics, uid);
      setUserProperties(analytics, { subscription_tier: tier.toLowerCase() });
    } catch {
      // no-op
    }
  })();
}

/** 사이드바 뷰 전환 추적 — 모바일 앱의 screen_view 와 통일 (BigQuery 통합 조회용) */
export function trackScreen(path: string): void {
  void trackEvent('screen_view', {
    firebase_screen: path,
    firebase_screen_class: 'MainPage',
  });
}

/** 로그인 성공 추적 */
export function trackLogin(method: 'google' | 'apple'): void {
  void trackEvent('login', { method });
}
